import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Loader2, MapPinned } from "lucide-react";
import {
  GROUND_PIN_FROM_FIELD,
  GROUND_PIN_FROM_MAP,
  formWithPin,
  groundEditDiff,
  groundEditErrors,
  groundEditForm,
  GROUND_NAME_MAX,
  isGroundPin,
  pinFromForm,
  samePin,
} from "../lib/groundEdit";
import { reverseGeocodePlace } from "../lib/geocode";
import { PLACE_TYPES, PLACE_TYPE_META } from "../lib/placeType";
import ArchiveToggle from "./ArchiveToggle";

/**
 * How long the pin must sit still before we ask Nominatim where it is.
 *
 * A drag emits nothing until it ends, but a run of small corrections ("no,
 * a bit left") is several `dragend`s in a couple of seconds, and Nominatim's
 * usage policy is one request a second. Waiting for the pin to settle turns
 * a flurry of nudges into one lookup, and the lookup we skip is one whose
 * answer was about to be overwritten anyway.
 */
export const PIN_GEOCODE_SETTLE_MS = 700;

/**
 * Issue #131 Tier 1 — the creator's own correction form, rendered inside the
 * ground detail dialog.
 *
 * "I pinned my club 300m into the neighbour's paddock" is the case this
 * exists for, and it is the overwhelmingly common one. It lives in the
 * detail dialog rather than on a page of its own because that is where a
 * person is already standing when they notice the pin is wrong — they opened
 * the ground to look at it.
 *
 * IT ONLY EVER SENDS WHAT CHANGED (`groundEditDiff`), and it cannot request
 * publication: `is_global`/`is_verified` are not fields here and the API's
 * patch schema is strict, so there is no shape of this form that promotes a
 * ground into the shared directory. That is the Venues epic's QA gate
 * (#316/#130), not an edit form.
 *
 * ISSUE #379 — THE MAP IS THE OTHER INPUT. The pin on the dialog's own map
 * above this form is draggable while the form is open, and a tap anywhere on
 * that map moves it; both arrive here as the `pin` prop and are written into
 * the two coordinate fields. The fields stayed: they are the route a
 * keyboard or a screen reader can take (leaflet gives a marker focus, but
 * not a drag), and the right one when someone has a surveyed coordinate. So
 * this component holds ONE position with two ways in, and `pin.via` says
 * which way a given change came — a `field` pin is this form's own typing
 * echoed back off the map and is applied to nothing.
 *
 * A REFUSAL IS SHOWN, NOT SWALLOWED. The server owns conditions this
 * component cannot see (another player has bookmarked or played here — see
 * `lib/groundEdit.js`), so a 403 is an expected outcome rather than a bug,
 * and its `detail` is written to be read by whoever pressed Save.
 *
 * `hideIndoor` (issue #1008, D203.1) — false by default, so this panel's
 * legacy/admin callers (`AdminVenues.jsx`, the pre-#766 grounds map detail
 * dialog) are byte-identical. `GroundCard.jsx` ("Correct details" on the
 * flag-on `newUi("ground-card")` ground card) passes
 * `hideIndoor={newUi("ground-card")}`, read once at `pages/GroundsMap.jsx`'s
 * own `newUi("ground-card") && selectedCardGround` gate and threaded down —
 * not read directly in here or in `GroundCard.jsx`, because `GroundCard`
 * itself never renders unless that gate already passed, and a raw
 * `newUi(...)` call inside this SHARED file would also apply to
 * `AdminVenues.jsx`'s unconditional mount the moment the runtime default
 * (D202, unset-means-ON) makes the flag true — hiding Indoor from an admin
 * who has no flag-gated "new-look" surface at all.
 *
 * HELD (the issue's own "Must hold"): a ground whose type is ALREADY
 * "indoor" keeps the Indoor button, even with `hideIndoor` true — taken
 * from `ground.place_type` (the value this panel was opened with), not the
 * live `form.place_type`, so toggling away and back during one edit never
 * makes the button disappear out from under the person mid-session.
 */
export default function GroundEditPanel({ ground, pin = null, onPinChange, onSaved, onCancel, hideIndoor = false }) {
  const groundWasIndoor = ground.place_type === "indoor";
  const visiblePlaceTypes = hideIndoor && !groundWasIndoor ? PLACE_TYPES.filter((t) => t !== "indoor") : PLACE_TYPES;
  const [form, setForm] = useState(() => groundEditForm(ground));
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState("");
  /**
   * What the fields say RIGHT NOW, readable from an effect that must not
   * depend on them (see below). Assigned during render rather than in an
   * effect so it is never a render behind the state it mirrors.
   */
  const formRef = useRef(form);
  formRef.current = form;

  const errors = useMemo(() => groundEditErrors(form), [form]);
  const patch = useMemo(() => groundEditDiff(ground, form), [ground, form]);
  const nothingChanged = Object.keys(patch).length === 0;
  const invalid = Object.keys(errors).length > 0;

  /**
   * Issue #379 — a pin the MAP moved, applied to the fields.
   *
   * Keyed on the pin alone, never on the form: an effect that also watched
   * the form would re-run the moment it wrote to it, and its own cleanup
   * would abort the reverse-geocode it had just scheduled. The ref is what
   * lets it still ask the question that matters — "do the fields already say
   * this?" — which is the honest guard. ("Have I seen this pin object
   * before?" is not: drag away, type something, drag back to the first spot,
   * and the fields would keep the typed value under a marker that had
   * moved.)
   */
  useEffect(() => {
    if (!isGroundPin(pin) || pin.via !== GROUND_PIN_FROM_MAP) return undefined;
    if (samePin(pin, pinFromForm(formRef.current))) return undefined;
    setServerError("");
    setForm((f) => formWithPin(f, pin));

    // Best-effort city/country, exactly as the add flow does it for a
    // dropped pin. Only ever fills a field the lookup has an answer for —
    // moving a pin 300 m within the same town must not blank a city
    // somebody typed because Nominatim happened to be unreachable.
    const ac = new AbortController();
    const timer = setTimeout(() => {
      reverseGeocodePlace(pin.lat, pin.lng, ac.signal).then((place) => {
        if (!place) return;
        setForm((f) => ({
          ...f,
          city: place.city || f.city,
          country: place.country || f.country,
        }));
      });
    }, PIN_GEOCODE_SETTLE_MS);
    return () => {
      clearTimeout(timer);
      ac.abort();
    };
  }, [pin]);

  const set = (field) => (e) => {
    setServerError("");
    const value = e.target.value;
    const next = { ...form, [field]: value };
    setForm(next);
    // Issue #379, the other direction: a typed coordinate moves the marker
    // too, or the map and the fields would be two disagreeing answers to one
    // question. Marked `field` so the effect above knows this is the form's
    // own value coming back and leaves the text alone — reformatting
    // "-42.4" to "-42.400000" under a cursor mid-typing is the bug that
    // shape of sync usually ships with.
    if (field === "latitude" || field === "longitude") {
      const moved = pinFromForm(next);
      if (moved && onPinChange && !samePin(moved, pinFromForm(form))) {
        onPinChange({ ...moved, via: GROUND_PIN_FROM_FIELD });
      }
    }
  };

  async function submit(e) {
    e.preventDefault();
    if (invalid || nothingChanged || saving) return;
    setSaving(true);
    setServerError("");
    try {
      const res = await api.patch(`/grounds/${ground.ground_id}`, patch);
      onSaved(res.data);
    } catch (err) {
      setServerError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  const field = (name, label, extra = {}) => (
    <div className="flex flex-col gap-1 min-w-0">
      <label className="text-xs font-semibold text-slate-600" htmlFor={`ground-edit-${name}`}>
        {label}
      </label>
      <Input
        id={`ground-edit-${name}`}
        data-testid={`ground-edit-${name}`}
        value={form[name]}
        onChange={set(name)}
        {...extra}
      />
      {errors[name] && <span className="text-xs text-red-600">{errors[name]}</span>}
    </div>
  );

  return (
    <form className="mt-5 border-t border-slate-200 pt-4" onSubmit={submit} data-testid="ground-edit-form">
      <h3 className="font-display font-bold text-sm text-slate-900 mb-1">Correct this ground</h3>
      <p className="text-xs text-slate-500 mb-3">
        Fix the name, the place type, or nudge the pin. Everyone who saves this ground sees the change.
      </p>

      {/* Issue #379: the map above is now an input, and an affordance nobody
          is told about is one nobody uses — the whole reason this ticket
          exists is that the only route people could FIND was the number
          fields. Named here, next to those fields, rather than only as an
          overlay on the map, so it is read whichever end you start from. */}
      <p className="text-xs text-slate-600 mb-3 flex items-start gap-1.5" data-testid="ground-edit-pin-help">
        <MapPinned size={14} className="shrink-0 mt-0.5 text-[#0B2E59]" />
        <span>
          Drag the pin on the map above, or tap where it should be — the coordinates below follow.
          You can still type them if you know them.
        </span>
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {field("name", "Name", { maxLength: GROUND_NAME_MAX })}
        {/* Issue #136 — a wrong type is a mis-pin of the same kind as a
            wrong coordinate, so it is correctable on the same terms. Same
            three buttons as the add dialog rather than a select, so the two
            places a person answers this question look like each other; the
            difference is that here one is always already pressed, because
            the ground already has a type. */}
        <div className="flex flex-col gap-1 min-w-0" data-testid="ground-edit-place-type">
          <span className="text-xs font-semibold text-slate-600">Place type</span>
          <div className="flex flex-wrap gap-1.5">
            {visiblePlaceTypes.map((t) => {
              const active = form.place_type === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => { setServerError(""); setForm((f) => ({ ...f, place_type: t })); }}
                  aria-pressed={active}
                  data-testid={`ground-edit-place-type-${t}`}
                  className={`rounded-md border px-2.5 min-h-11 text-xs font-semibold transition ${active
                    ? "border-[#0B2E59] bg-[#0B2E5908] text-[#0B2E59]"
                    : "border-slate-200 text-slate-600 hover:border-slate-400"}`}
                >
                  {PLACE_TYPE_META[t].label}
                </button>
              );
            })}
          </div>
          {errors.place_type && <span className="text-xs text-red-600">{errors.place_type}</span>}
        </div>
        {field("city", "City")}
        {field("country", "Country")}
        <div className="grid grid-cols-2 gap-3 min-w-0">
          {field("latitude", "Latitude", { inputMode: "decimal" })}
          {field("longitude", "Longitude", { inputMode: "decimal" })}
        </div>
      </div>

      {serverError && (
        <p className="mt-3 text-sm text-red-600" data-testid="ground-edit-error" role="alert">
          {serverError}
        </p>
      )}

      <div className="mt-4 flex items-center gap-2">
        <Button
          type="submit"
          disabled={saving || invalid || nothingChanged}
          data-testid="ground-edit-save"
          style={{ background: "#0B2E59" }}
        >
          {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}
          Save changes
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} data-testid="ground-edit-cancel">
          Cancel
        </Button>
      </div>

      {/* Issue #166 FAC-11 — archive lives WITH the corrections, because it
          is one: a statement about the venue, on the venue's record, behind
          the same gate. There is deliberately no delete here and none on
          the server — archive and merge are a ground's only retirement
          verbs (#131 / FAC-12) — so this control is the whole lifecycle
          surface. `onSaved` hands the server's row up exactly as a field
          correction does; the map badge and the pickers follow from it. */}
      <div className="mt-4 border-t border-slate-200 pt-3">
        <ArchiveToggle
          basePath={`/grounds/${ground.ground_id}`}
          archived={Boolean(ground.archived_at)}
          noun="ground"
          onDone={onSaved}
          testId="ground-archive"
        />
      </div>
    </form>
  );
}
